import "@/App.css";
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { motion, useInView } from "framer-motion";
import Particles from "@tsparticles/react";
import { loadSlim } from "@tsparticles/slim";
import {
  Menu, X, Instagram, Video, Globe, Target,
  ArrowRight, Check, Play, MessageCircle, Mail, Phone,
  TrendingUp, Sparkles, ChevronRight, ChevronDown, Music2,
  Facebook, Youtube, Search, AlertTriangle
} from "lucide-react";
import { BrowserRouter, Routes, Route, useNavigate, Link } from "react-router-dom";

// ============================================
// CONSTANTS
// ============================================
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_vale-spanish/artifacts/kohvyws1_Black%20and%20Beige%20Minimalist%20Aesthetic%20Modern%20Simple%20Typography%20Agency%20Logo%20%282%29.png";

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/81be5537-fd4f-479a-aca3-0fd4dcdc0cad/images/78fc50d7010eeae0ba7a45063d54225e443fe741e876d8ed4418698652868a04.jpeg";

const CONTACT = {
  phone: "+34 660 764 417",
  phone2: "+34 693 023 053",
  email: "aliadosagency@gmail.com",
  whatsapp: "34693023053",
};

const SOCIAL = {
  instagram: "https://www.instagram.com/_aliados.agency/",
  tiktok: "https://www.tiktok.com/@aliadosagency?_r=1&_t=ZG-99hh5DKpeFN",
};

const SCARCITY_TEXT = "Aceptamos un máximo de 3 nuevos clientes al mes para garantizar resultados";

// ============================================
// HELPERS
// ============================================
const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

const trackLead = () => {
  if (typeof window !== "undefined" && window.fbq) {
    window.fbq("track", "Lead", { content_name: "Formulario de contacto" });
  }
};

// ============================================
// ANIMATION VARIANTS
// ============================================
const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};
const stagger = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.12 } },
};

// ============================================
// PARTICLES BACKGROUND
// ============================================
const ParticlesBackground = () => {
  const particlesInit = useCallback(async (engine) => {
    await loadSlim(engine);
  }, []);

  const options = useMemo(() => ({
    fullScreen: false,
    background: { color: { value: "transparent" } },
    fpsLimit: 60,
    particles: {
      color: { value: "#0ea5e9" },
      links: { color: "#0ea5e9", distance: 150, enable: true, opacity: 0.18, width: 1 },
      move: { enable: true, outModes: { default: "bounce" }, speed: 0.7 },
      number: { density: { enable: true, area: 1000 }, value: 55 },
      opacity: { value: 0.3 },
      shape: { type: "circle" },
      size: { value: { min: 1, max: 3 } },
    },
    detectRetina: true,
  }), []);

  return (
    <Particles id="tsparticles" className="absolute inset-0 z-0" init={particlesInit} options={options} />
  );
};

// ============================================
// CTA BUTTON
// ============================================
const CtaButton = ({ children, onClick, className = "", testid, size = "lg" }) => {
  const sizes = {
    lg: "px-9 py-4 text-base md:text-lg",
    md: "px-7 py-3 text-sm md:text-base",
  };
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`group relative inline-flex items-center justify-center gap-2 rounded-full font-bold text-white bg-sky-500 hover:bg-sky-400 transition-all duration-300 hover:-translate-y-0.5 shadow-[0_0_35px_rgba(14,165,233,0.45)] hover:shadow-[0_0_55px_rgba(14,165,233,0.7)] ${sizes[size]} ${className}`}
    >
      {children}
      <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
    </button>
  );
};

// ============================================
// HEADER (scarcity bar + sticky nav)
// ============================================
const WHAT_WE_DO = [
  { id: "qh-meta", label: "Meta Ads (Facebook e Instagram)" },
  { id: "qh-google", label: "Google Ads" },
  { id: "qh-youtube", label: "YouTube Ads" },
  { id: "qh-seo", label: "SEO y SEM" },
  { id: "qh-rrss", label: "Gestión de Redes Sociales y Contenido" },
];

const Header = () => {
  const [open, setOpen] = useState(false);
  const [ddOpen, setDdOpen] = useState(false);
  const [mobileDdOpen, setMobileDdOpen] = useState(false);

  const nav = [
    { label: "Resultados", id: "resultados" },
    { label: "Proceso", id: "proceso" },
    { label: "Nosotros", id: "nosotros" },
  ];

  const go = (id) => { scrollToId(id); setOpen(false); setDdOpen(false); setMobileDdOpen(false); };

  return (
    <header className="fixed top-0 left-0 right-0 z-50">
      {/* Scarcity bar */}
      <div className="bg-sky-500 text-white text-center text-xs sm:text-sm font-semibold py-2 px-4 flex items-center justify-center gap-2" data-testid="scarcity-bar">
        <Sparkles size={15} className="shrink-0" />
        <span>{SCARCITY_TEXT}</span>
      </div>

      {/* Nav */}
      <nav className="bg-black/70 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 h-16 flex items-center justify-between">
          <button onClick={() => scrollToId("hero")} data-testid="nav-logo">
            <img src={LOGO_URL} alt="Aliados Agency" className="h-8 md:h-12 w-auto" />
          </button>

          <div className="hidden md:flex items-center gap-7">
            <button onClick={() => go("servicios")} data-testid="nav-servicios" className="text-sm font-medium text-zinc-300 hover:text-white transition-colors">
              Servicios
            </button>

            {/* Dropdown Qué hacemos */}
            <div className="relative" onMouseEnter={() => setDdOpen(true)} onMouseLeave={() => setDdOpen(false)}>
              <button onClick={() => setDdOpen(!ddOpen)} data-testid="nav-que-hacemos" className="flex items-center gap-1 text-sm font-medium text-zinc-300 hover:text-white transition-colors">
                Qué hacemos <ChevronDown size={15} className={`transition-transform duration-200 ${ddOpen ? "rotate-180" : ""}`} />
              </button>
              {ddOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 pt-3" data-testid="nav-dropdown">
                  <div className="w-72 bg-[#0a0a0a] border border-white/10 rounded-2xl p-2 shadow-2xl shadow-black/60">
                    {WHAT_WE_DO.map((s) => (
                      <button
                        key={s.id}
                        onClick={() => go(s.id)}
                        data-testid={`dropdown-${s.id}`}
                        className="w-full text-left px-4 py-2.5 rounded-xl text-sm text-zinc-300 hover:text-white hover:bg-sky-500/10 transition-colors"
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {nav.map((n) => (
              <button
                key={n.id}
                onClick={() => go(n.id)}
                data-testid={`nav-${n.id}`}
                className="text-sm font-medium text-zinc-300 hover:text-white transition-colors"
              >
                {n.label}
              </button>
            ))}
            <CtaButton size="md" onClick={() => scrollToId("contacto")} testid="nav-cta">
              Quiero mi auditoría gratuita
            </CtaButton>
          </div>

          <div className="flex items-center gap-2 md:hidden">
            <button
              onClick={() => scrollToId("contacto")}
              data-testid="mobile-cta-top"
              className="rounded-full bg-sky-500 hover:bg-sky-400 text-white text-[11px] font-bold px-3 py-1.5 shadow-[0_0_16px_rgba(14,165,233,0.35)] transition-colors whitespace-nowrap"
            >
              Quiero mi auditoría gratuita
            </button>
            <button className="text-white" onClick={() => setOpen(!open)} data-testid="nav-toggle" aria-label="Menú">
              {open ? <X size={26} /> : <Menu size={26} />}
            </button>
          </div>
        </div>

        {open && (
          <div className="md:hidden bg-black/95 border-t border-white/10 px-5 py-5 flex flex-col gap-3 max-h-[75vh] overflow-y-auto" data-testid="mobile-menu">
            <button onClick={() => go("servicios")} className="text-left text-zinc-200 font-medium py-1">Servicios</button>

            <div>
              <button onClick={() => setMobileDdOpen(!mobileDdOpen)} data-testid="mobile-nav-que-hacemos" className="w-full flex items-center justify-between text-zinc-200 font-medium py-1">
                Qué hacemos <ChevronDown size={16} className={`transition-transform duration-200 ${mobileDdOpen ? "rotate-180" : ""}`} />
              </button>
              {mobileDdOpen && (
                <div className="pl-4 mt-2 flex flex-col gap-2 border-l border-white/10">
                  {WHAT_WE_DO.map((s) => (
                    <button key={s.id} onClick={() => go(s.id)} data-testid={`mobile-dropdown-${s.id}`} className="text-left text-sm text-zinc-400 hover:text-white py-1">
                      {s.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {nav.map((n) => (
              <button key={n.id} onClick={() => go(n.id)} className="text-left text-zinc-200 font-medium py-1">
                {n.label}
              </button>
            ))}
            <CtaButton size="md" onClick={() => go("contacto")} className="w-full" testid="mobile-nav-cta">
              Quiero mi auditoría gratuita
            </CtaButton>
          </div>
        )}
      </nav>
    </header>
  );
};

// ============================================
// RESERVED SPACE placeholder
// ============================================
const Reserved = ({ text, className = "" }) => (
  <div
    className={`rounded-2xl border-2 border-dashed border-sky-500/40 bg-sky-500/5 px-6 py-5 ${className}`}
    data-testid="reserved-space"
  >
    <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-sky-400 mb-1">Espacio reservado</p>
    <p className="text-zinc-400 text-sm">{text}</p>
  </div>
);

// ============================================
// SECTION 1 — HERO
// ============================================
const Hero = () => {
  return (
    <section id="hero" className="relative min-h-screen flex items-center overflow-hidden pt-32 pb-20">
      {/* Background image */}
      <div
        className="absolute inset-0 z-0 bg-cover [background-position:42%_30%] md:[background-position:center]"
        style={{ backgroundImage: `url(${HERO_BG})` }}
        data-testid="hero-bg"
      />
      {/* Dark overlays for readability */}
      <div className="absolute inset-0 z-[1] bg-gradient-to-b from-[#050505]/75 via-[#050505]/45 to-[#050505]/95 md:from-[#050505]/90 md:via-[#050505]/70 md:to-[#050505]" />
      <div className="absolute inset-0 z-[1] bg-[#050505]/25 md:bg-[#050505]/45" />

      <div className="relative z-10 max-w-5xl mx-auto px-5 sm:px-6 text-center">
        <motion.div variants={stagger} initial="hidden" animate="visible">
          {/* Centered logo */}
          <motion.img
            variants={fadeUp}
            src={LOGO_URL}
            alt="Aliados Agency"
            className="h-44 md:h-60 lg:h-64 w-auto mx-auto mb-8 drop-shadow-[0_0_25px_rgba(0,0,0,0.6)]"
            data-testid="hero-logo"
          />

          {/* Small brand label below logo */}
          <motion.div variants={fadeUp} className="flex items-center justify-center gap-2 mb-6" data-testid="hero-tagline">
            <Facebook size={15} className="text-sky-400" />
            <Instagram size={15} className="text-sky-400" />
            <span className="text-xs md:text-sm font-bold uppercase tracking-[0.25em] text-sky-400">Agencia de Meta Ads</span>
          </motion.div>

          <motion.h1
            variants={fadeUp}
            className="text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight leading-[1.05] mb-6"
          >
            LLENAMOS TU NEGOCIO DE CLIENTES{" "}
            <span className="text-gradient">CON PUBLICIDAD EN INSTAGRAM Y FACEBOOK</span>
          </motion.h1>

          <motion.p variants={fadeUp} className="text-base md:text-lg text-zinc-300 max-w-2xl mx-auto mb-9">
            Campañas de Meta Ads para negocios que quieren clientes, no seguidores. Tú atiendes, nosotros te traemos la gente. Resultados medibles cada mes o no seguimos.
          </motion.p>

          <motion.div variants={fadeUp} className="flex flex-col items-center gap-3">
            <CtaButton onClick={() => scrollToId("contacto")} testid="hero-cta">
              Quiero mi auditoría gratuita
            </CtaButton>
            <p className="text-xs text-sky-400 font-semibold flex items-center gap-1.5 text-center justify-center">
              <Sparkles size={13} className="shrink-0" /> Aceptamos un máximo de 3 nuevos clientes al mes para garantizar resultados
            </p>
          </motion.div>

          {/* Client credibility */}
          <motion.div variants={fadeUp} className="mt-10">
            <p className="text-xs md:text-sm text-zinc-400 max-w-xl mx-auto leading-relaxed" data-testid="hero-clients-text">Trabajamos con negocios de sectores muy variados: restauración, ocio nocturno, automoción, servicios e instituciones.</p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// ============================================
// SECTION 2 — SOCIAL PROOF (numbers)
// ============================================
const CountUp = ({ value, decimals = 0, prefix = "", suffix = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let raf;
    const start = performance.now();
    const duration = 1600;
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(value * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
      else setDisplay(value);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, value]);

  const formatted = display.toLocaleString("es-ES", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    useGrouping: "always",
  });
  return <span ref={ref}>{prefix}{formatted}{suffix}</span>;
};

const SocialProof = () => {
  const metrics = [
    { value: 1000000, prefix: "+", line: "Visualizaciones generadas para negocios en redes sociales", label: "Acumulado de todas las cuentas gestionadas" },
    { value: 1500, suffix: "€", line: "Facturados en un solo día con una única campaña promocional", label: "Sector restauración · 150 unidades vendidas en una jornada" },
    { value: 107565, line: "Personas distintas alcanzadas en 60 días con un solo negocio", label: "Más que habitantes tiene la ciudad donde opera" },
    { value: 1597, line: "Clics al enlace de contacto generados en 60 días", label: "Tráfico real hacia el negocio, no solo visualizaciones" },
  ];

  return (
    <section id="resultados" className="relative py-24 px-5 sm:px-6">
      <div className="max-w-6xl mx-auto">
        <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-12 max-w-2xl mx-auto">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-sky-500 mb-3">Prueba social</p>
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-4">Los números hablan por nosotros</h2>
          <p className="text-sm text-zinc-500">Datos reales de campañas y contenido que gestionamos. Todo ello conseguido únicamente con contenido orgánico, antes siquiera de activar publicidad de pago.</p>
        </motion.div>

        {/* Metric cards */}
        <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }} className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {metrics.map((m, i) => (
            <motion.div
              key={i}
              variants={fadeUp}
              className="group rounded-2xl bg-[#0a0a0a] border border-white/10 p-4 md:p-7 text-center transition-all duration-300 hover:border-sky-500/60 hover:-translate-y-1 hover:shadow-[0_0_30px_rgba(14,165,233,0.15)]"
              data-testid={`metric-${i}`}
            >
              <p className="text-2xl sm:text-3xl md:text-4xl font-black text-gradient mb-3 whitespace-nowrap">
                <CountUp value={m.value} prefix={m.prefix || ""} suffix={m.suffix || ""} />
              </p>
              <p className="text-xs sm:text-sm md:text-base text-white font-medium leading-snug mb-3">{m.line}</p>
              <p className="text-[10px] sm:text-[11px] text-zinc-500 leading-snug">{m.label}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* "El dato que lo cambia todo" */}
        <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="mt-12 max-w-4xl mx-auto">
          <h3 className="text-center text-2xl md:text-3xl font-bold tracking-tight mb-8">El contenido abre la puerta. La publicidad la derriba.</h3>
          <div className="grid md:grid-cols-2 gap-5 items-stretch">
            <div className="rounded-2xl bg-[#0a0a0a] border border-white/10 p-7 flex gap-4">
              <div className="w-11 h-11 rounded-xl bg-amber-500/10 text-amber-400 flex items-center justify-center shrink-0">
                <AlertTriangle size={22} />
              </div>
              <p className="text-zinc-300 leading-relaxed">El contenido orgánico tiene un techo: llega a quien el algoritmo decide, cuando decide, y sin control sobre a quién. Y alcanzar a 100.000 personas con publicidad requiere entre <strong className="text-white">500€ y 1.500€</strong> de inversión en la plataforma, que sin estrategia se gasta sin traer un solo cliente.</p>
            </div>
            <div className="rounded-2xl bg-sky-500/10 border border-sky-500/50 shadow-[0_0_30px_rgba(14,165,233,0.2)] p-7 flex items-center">
              <p className="text-lg md:text-xl font-bold text-white">Con <span className="text-gradient">Meta Ads</span> eliges exactamente a quién llegas, cuándo y con qué mensaje. Es la diferencia entre esperar clientes y salir a buscarlos.</p>
            </div>
          </div>
          <p className="text-center text-xs text-zinc-500 mt-4 max-w-2xl mx-auto">Inversión publicitaria estimada según los CPM medios de Meta en España (2026). No incluye honorarios de gestión.</p>
          <p className="text-center text-lg md:text-2xl font-semibold mt-8 max-w-2xl mx-auto">Estos resultados son nuestro punto de partida, no nuestro techo.</p>
        </motion.div>

        <div className="text-center mt-10">
          <CtaButton onClick={() => scrollToId("contacto")} testid="socialproof-cta">
            Quiero mi auditoría gratuita
          </CtaButton>
        </div>
      </div>
    </section>
  );
};

// ============================================
// SECTION 3 — PROBLEM
// ============================================
const Problem = () => {
  const pains = [
    { icon: TrendingUp, text: "Tienes días buenos y días vacíos, y no sabes de qué depende." },
    { icon: Target, text: "Has probado a poner anuncios por tu cuenta y no sabes si el dinero sirvió de algo." },
    { icon: Instagram, text: "Tu competencia aparece constantemente en Instagram y Facebook, y tú no." },
  ];

  return (
    <section className="relative py-24 px-5 sm:px-6">
      <div className="max-w-4xl mx-auto text-center">
        <motion.h2
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-14 leading-tight"
        >
          ¿Tu negocio depende del boca a boca<br className="hidden sm:block" /> y de que entre gente por la puerta?
        </motion.h2>

        <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }} className="grid md:grid-cols-3 gap-5 mb-14">
          {pains.map((p, i) => (
            <motion.div key={i} variants={fadeUp} className="bento-card p-7 text-left" data-testid={`pain-${i}`}>
              <div className="w-12 h-12 rounded-xl bg-red-500/10 text-red-400 flex items-center justify-center mb-4">
                <p.icon size={24} />
              </div>
              <p className="text-zinc-300 leading-relaxed">{p.text}</p>
            </motion.div>
          ))}
        </motion.div>

        <motion.p variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-2xl md:text-3xl font-bold">
          Eso tiene solución. <span className="text-gradient">Y nosotros la tenemos.</span>
        </motion.p>
      </div>
    </section>
  );
};

// ============================================
// SECTION 4 — SERVICES
// ============================================
const Services = () => {
  const services = [
    { icon: Instagram, title: "Gestión de Redes Sociales", desc: "Instagram y TikTok gestionados con estrategia para hacer crecer tu comunidad y tus ventas." },
    { icon: Video, title: "Producción de Contenido Cinematográfico", desc: "Vídeos que enamoran y paran el scroll, grabados y editados con calidad profesional." },
    { icon: Target, title: "Publicidad Digital", desc: "Campañas de Meta Ads y Google Ads que atraen clientes reales y rentables." },
    { icon: Globe, title: "Diseño Web que Convierte", desc: "Páginas rápidas y persuasivas diseñadas con un único objetivo: que te contacten." },
  ];

  return (
    <section id="servicios" className="relative py-24 px-5 sm:px-6">
      <div className="max-w-6xl mx-auto">
        <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-12">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-sky-500 mb-3">Servicios</p>
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight">Todo lo que tu negocio necesita para crecer</h2>
        </motion.div>

        <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }} className="grid sm:grid-cols-2 gap-5">
          {services.map((s, i) => (
            <motion.div key={i} variants={fadeUp} className="bento-card p-8 flex flex-col" data-testid={`service-${i}`}>
              <div className="w-14 h-14 rounded-xl bg-sky-500/15 text-sky-400 flex items-center justify-center mb-5">
                <s.icon size={28} />
              </div>
              <h3 className="text-xl font-bold mb-3">{s.title}</h3>
              <p className="text-zinc-400 leading-relaxed mb-6 flex-grow">{s.desc}</p>
              <button
                onClick={() => scrollToId("contacto")}
                data-testid={`service-cta-${i}`}
                className="inline-flex items-center gap-1.5 text-sky-400 font-semibold hover:text-sky-300 transition-colors self-start"
              >
                Saber más <ChevronRight size={16} />
              </button>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// ============================================
// SECTION 5 — PROCESS
// ============================================
const Process = () => {
  const steps = [
    { n: "1", title: "Auditoría gratuita", desc: "Analizamos tu presencia digital, tu competencia y tus oportunidades en 24 horas. Gratis y sin ningún compromiso." },
    { n: "2", title: "Plan a medida", desc: "Antes de que trabajes con nosotros, te diseñamos una estrategia personalizada según tu negocio, tus objetivos y tu presupuesto, con los resultados proyectados." },
    { n: "3", title: "Ejecución y resultados", desc: "Solo si te encaja, empezamos: creamos contenido, lanzamos campañas y te mostramos resultados medibles cada mes." },
  ];

  return (
    <section id="proceso" className="relative py-24 px-5 sm:px-6 bg-[#080808] border-y border-white/5">
      <div className="max-w-6xl mx-auto">
        <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-14 max-w-2xl mx-auto">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-sky-500 mb-3">Proceso</p>
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-4">Así trabajamos</h2>
          <p className="text-base md:text-lg text-zinc-400">Antes de trabajar juntos, te diseñamos un plan a medida para tu negocio. Sin compromiso y sin coste.</p>
        </motion.div>

        <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }} className="grid md:grid-cols-3 gap-6 mb-14">
          {steps.map((s, i) => (
            <motion.div key={i} variants={fadeUp} className="relative bento-card p-8" data-testid={`step-${i}`}>
              <div className="text-6xl font-black text-sky-500/20 absolute top-4 right-6">{s.n}</div>
              <div className="w-12 h-12 rounded-full bg-sky-500 text-white flex items-center justify-center font-bold text-lg mb-5">
                {s.n}
              </div>
              <h3 className="text-xl font-bold mb-3">{s.title}</h3>
              <p className="text-zinc-400 leading-relaxed">{s.desc}</p>
            </motion.div>
          ))}
        </motion.div>

        <div className="text-center">
          <CtaButton onClick={() => scrollToId("contacto")} testid="process-cta">
            Empieza con tu auditoría gratuita
          </CtaButton>
        </div>
      </div>
    </section>
  );
};

// ============================================
// SECTION 6 — WHO WE ARE
// ============================================
const WhoWeAre = () => {
  return (
    <section id="nosotros" className="relative py-24 px-5 sm:px-6">
      <div className="max-w-3xl mx-auto text-center">
        <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-sky-500 mb-3">Quiénes somos</p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-6 leading-tight">
            Alejandro y Pedro. Sin despacho, sin trajes, sin excusas.
          </h2>
          <div className="space-y-4 text-zinc-400 leading-relaxed text-base md:text-lg">
            <p>
              Somos dos socios de Melilla que decidimos montar algo real en vez de esperar a que nos llegara el momento perfecto.
            </p>
            <p>
              Empezamos desde cero, sin inversores, sin contactos, sin experiencia previa. Solo con ganas, una cámara y la convicción de que los negocios merecen un marketing que realmente funcione.
            </p>
            <p>
              Hoy gestionamos campañas para negocios reales, con resultados reales y medibles. No te vamos a prometer la luna. Te vamos a mostrar los datos.
            </p>
            <p className="text-white font-semibold">
              Y si los datos no convencen, no firmamos nada.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// ============================================
// SECTION 7 — INTERMEDIATE CTA
// ============================================
const IntermediateCTA = () => {
  return (
    <section className="relative py-16 px-5 sm:px-6 bg-sky-500 overflow-hidden">
      <div className="max-w-4xl mx-auto text-center relative z-10">
        <motion.h2 variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-2xl md:text-4xl font-bold text-white mb-8 leading-tight">
          Tu competencia ya se está anunciando. ¿Y tú?
        </motion.h2>
        <button
          onClick={() => scrollToId("contacto")}
          data-testid="intermediate-cta"
          className="inline-flex items-center gap-2 rounded-full font-bold text-sky-600 bg-white hover:bg-zinc-100 px-9 py-4 text-base md:text-lg transition-all duration-300 hover:-translate-y-0.5 shadow-xl"
        >
          Quiero mi auditoría gratuita <ArrowRight size={18} />
        </button>
      </div>
    </section>
  );
};

// ============================================
// SECTION 8 — LEAD FORM
// ============================================
const LeadForm = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [accepted, setAccepted] = useState(false);

  const update = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!accepted) { setError("Debes aceptar la Política de Privacidad para continuar."); return; }
    setError("");
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/lead`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("bad response");
      trackLead();
      navigate("/gracias");
    } catch (err) {
      setError("No se ha podido enviar. Inténtalo de nuevo o escríbenos por WhatsApp.");
      setSubmitting(false);
    }
  };

  const inputCls = "w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder-zinc-500 focus:outline-none focus:border-sky-500 focus:bg-white/[0.05] transition-colors";

  return (
    <section id="contacto" className="relative py-24 px-5 sm:px-6 overflow-hidden">
      <div className="glow-orb bg-sky-500/15 w-[600px] h-[600px] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />

      <div className="relative z-10 max-w-2xl mx-auto">
        <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-10">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-sky-500 mb-3">Contacto</p>
          <h2 className="text-2xl md:text-4xl font-bold tracking-tight leading-tight">
            Cuéntanos qué necesitas y te preparamos una propuesta sin compromiso en menos de 24 horas
          </h2>
        </motion.div>

        <motion.form
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          onSubmit={handleSubmit}
          className="bento-card p-6 sm:p-8 space-y-4"
          data-testid="lead-form"
        >
          <input name="name" value={form.name} onChange={update} required placeholder="Nombre y apellidos" className={inputCls} data-testid="input-name" />
          <input name="phone" value={form.phone} onChange={update} required type="tel" placeholder="Número de teléfono" className={inputCls} data-testid="input-phone" />
          <input name="email" value={form.email} onChange={update} required type="email" placeholder="Correo electrónico" className={inputCls} data-testid="input-email" />
          <textarea name="message" value={form.message} onChange={update} required rows={4} placeholder="Cuéntanos qué servicio necesitas y cómo podemos ayudarte" className={`${inputCls} resize-none`} data-testid="input-message" />

          {error && <p className="text-red-400 text-sm" data-testid="form-error">{error}</p>}

          <label className="flex items-start gap-3 text-left text-xs text-zinc-400 leading-relaxed cursor-pointer" data-testid="privacy-consent-label">
            <input
              type="checkbox"
              name="accepted"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              required
              data-testid="input-consent"
              className="mt-0.5 h-4 w-4 shrink-0 accent-sky-500"
            />
            <span>He leído y acepto la <Link to="/privacidad" target="_blank" className="text-sky-400 underline hover:text-sky-300">Política de Privacidad</Link> y el tratamiento de mis datos para recibir información comercial.</span>
          </label>

          <button
            type="submit"
            disabled={submitting || !accepted}
            data-testid="submit-lead"
            className="w-full inline-flex items-center justify-center gap-2 rounded-full font-bold text-white bg-sky-500 hover:bg-sky-400 py-4 text-lg transition-all duration-300 hover:-translate-y-0.5 shadow-[0_0_35px_rgba(14,165,233,0.45)] disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {submitting ? "Enviando..." : "Enviar mi propuesta"}
            {!submitting && <ArrowRight size={18} />}
          </button>

          <p className="text-center text-xs text-zinc-500 flex items-center justify-center gap-1.5">
            <Check size={14} className="text-sky-400" /> Sin compromiso. Te respondemos en menos de 24 horas.
          </p>
        </motion.form>
      </div>
    </section>
  );
};

// ============================================
// SECTION 9 — FINAL CTA
// ============================================
const FinalCTA = () => {
  return (
    <section className="relative py-24 px-5 sm:px-6 overflow-hidden">
      <div className="glow-orb bg-sky-500/10 w-[500px] h-[500px] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
      <div className="relative z-10 max-w-3xl mx-auto text-center">
        <motion.h2 variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-3xl md:text-5xl font-bold tracking-tight mb-8">
          ¿Listo para que tu negocio <span className="text-gradient">crezca de verdad?</span>
        </motion.h2>
        <CtaButton onClick={() => scrollToId("contacto")} testid="final-cta">
          Quiero más clientes ahora
        </CtaButton>
      </div>
    </section>
  );
};

// ============================================
// FOOTER
// ============================================
const Footer = () => {
  return (
    <footer className="py-12 px-5 sm:px-6 border-t border-white/10 bg-[#050505]">
      <div className="max-w-6xl mx-auto flex flex-col items-center gap-6 text-center">
        <img src={LOGO_URL} alt="Aliados Agency" className="h-9 w-auto" data-testid="footer-logo" />

        <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-6 text-sm text-zinc-400">
          <a href={`mailto:${CONTACT.email}`} className="hover:text-white transition-colors flex items-center gap-2" data-testid="footer-email">
            <Mail size={15} /> {CONTACT.email}
          </a>
          <a href={`tel:${CONTACT.phone.replace(/\s/g, "")}`} className="hover:text-white transition-colors flex items-center gap-2" data-testid="footer-phone">
            <Phone size={15} /> {CONTACT.phone}
          </a>
          <a href={`tel:${CONTACT.phone2.replace(/\s/g, "")}`} className="hover:text-white transition-colors flex items-center gap-2" data-testid="footer-phone-2">
            <Phone size={15} /> {CONTACT.phone2}
          </a>
        </div>

        <div className="flex items-center gap-4">
          <a href={SOCIAL.instagram} target="_blank" rel="noreferrer" data-testid="footer-instagram" className="w-11 h-11 rounded-full bg-white/[0.04] border border-white/10 flex items-center justify-center text-zinc-300 hover:text-white hover:border-sky-500/50 hover:bg-sky-500/10 transition-all">
            <Instagram size={19} />
          </a>
          <a href={SOCIAL.tiktok} target="_blank" rel="noreferrer" data-testid="footer-tiktok" className="w-11 h-11 rounded-full bg-white/[0.04] border border-white/10 flex items-center justify-center text-zinc-300 hover:text-white hover:border-sky-500/50 hover:bg-sky-500/10 transition-all">
            <Music2 size={19} />
          </a>
        </div>

        <div className="flex flex-wrap justify-center gap-5 text-xs text-zinc-600">
          <Link to="/aviso-legal" className="hover:text-zinc-400 transition-colors">Aviso legal</Link>
          <Link to="/privacidad" className="hover:text-zinc-400 transition-colors">Privacidad</Link>
          <Link to="/cookies" className="hover:text-zinc-400 transition-colors">Cookies</Link>
        </div>

        <p className="text-zinc-700 text-xs">© 2026 Aliados Agency · España</p>
      </div>
    </footer>
  );
};

// ============================================
// FLOATING WHATSAPP
// ============================================
const WhatsAppFloat = () => (
  <a
    href={`https://wa.me/${CONTACT.whatsapp}`}
    target="_blank"
    rel="noreferrer"
    data-testid="whatsapp-float"
    className="fixed bottom-5 right-5 z-50 w-14 h-14 rounded-full bg-[#25D366] flex items-center justify-center shadow-[0_8px_30px_rgba(37,211,102,0.5)] hover:scale-110 transition-transform"
    aria-label="WhatsApp"
  >
    <MessageCircle size={28} className="text-white" fill="white" />
  </a>
);

// ============================================
// THANK YOU PAGE — /gracias
// ============================================
const GraciasPage = () => {
  return (
    <div className="min-h-screen bg-[#050505] text-white flex flex-col">
      {/* Scarcity bar */}
      <div className="bg-sky-500 text-white text-center text-xs sm:text-sm font-semibold py-2 px-4 flex items-center justify-center gap-2">
        <Sparkles size={15} /> <span>{SCARCITY_TEXT}</span>
      </div>

      <div className="flex-grow flex items-center justify-center px-5 py-16 relative overflow-hidden">
        <div className="glow-orb bg-sky-500/15 w-[500px] h-[500px] top-0 left-1/2 -translate-x-1/2" />

        <motion.div initial="hidden" animate="visible" variants={stagger} className="relative z-10 max-w-2xl mx-auto text-center">
          <motion.div variants={fadeUp} className="w-20 h-20 rounded-full bg-sky-500/15 text-sky-400 flex items-center justify-center mx-auto mb-8">
            <Check size={44} />
          </motion.div>

          <motion.h1 variants={fadeUp} className="text-3xl md:text-5xl font-bold tracking-tight mb-5">
            ¡Solicitud recibida! 🎉
          </motion.h1>

          <motion.p variants={fadeUp} className="text-zinc-400 text-base md:text-lg mb-10 max-w-xl mx-auto">
            Gracias por confiar en Aliados Agency. En menos de <strong className="text-white">24 horas</strong> uno de nuestros especialistas revisará tu solicitud y se pondrá en contacto contigo personalmente.
          </motion.p>

          {/* Reserved welcome video */}
          <motion.div variants={fadeUp} className="rounded-3xl border-2 border-dashed border-sky-500/40 bg-sky-500/5 aspect-video max-w-lg mx-auto flex flex-col items-center justify-center text-center px-6 mb-10" data-testid="reserved-video-gracias">
            <div className="w-16 h-16 rounded-full bg-sky-500/20 text-sky-400 flex items-center justify-center mb-4">
              <Play size={28} />
            </div>
            <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-sky-400 mb-1">Espacio reservado</p>
            <p className="text-zinc-400 text-sm">Aquí irá vuestro vídeo corto dando la bienvenida.</p>
          </motion.div>

          <motion.div variants={fadeUp} className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-10">
            <a href={SOCIAL.instagram} target="_blank" rel="noreferrer" data-testid="gracias-instagram" className="inline-flex items-center gap-2 rounded-full font-bold text-white bg-sky-500 hover:bg-sky-400 px-7 py-3.5 transition-all hover:-translate-y-0.5 shadow-[0_0_30px_rgba(14,165,233,0.4)]">
              <Instagram size={18} /> Síguenos en Instagram
            </a>
            <a href={SOCIAL.tiktok} target="_blank" rel="noreferrer" data-testid="gracias-tiktok" className="inline-flex items-center gap-2 rounded-full font-bold text-white bg-white/[0.05] border border-white/15 hover:bg-white/[0.08] px-7 py-3.5 transition-all hover:-translate-y-0.5">
              <Music2 size={18} /> Míranos en TikTok
            </a>
          </motion.div>

          <motion.a variants={fadeUp} href="/#resultados" data-testid="gracias-results-cta" className="inline-flex items-center gap-1.5 text-sky-400 font-semibold hover:text-sky-300 transition-colors">
            Mientras esperas, mira lo que hemos conseguido para nuestros clientes <ChevronRight size={16} />
          </motion.a>
        </motion.div>
      </div>

      <WhatsAppFloat />
    </div>
  );
};

// ============================================
// LEGAL PAGES
// ============================================
const LegalWrapper = ({ title, children }) => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#050505] text-white pt-16 pb-16 px-5">
      <div className="max-w-3xl mx-auto">
        <button onClick={() => navigate("/")} className="text-sky-400 hover:text-sky-300 mb-8 inline-flex items-center gap-2" data-testid="legal-back">
          <ChevronRight size={16} className="rotate-180" /> Volver al inicio
        </button>
        <h1 className="text-3xl font-bold mb-8">{title}</h1>
        <div className="space-y-6 text-zinc-300 leading-relaxed">{children}</div>
      </div>
    </div>
  );
};

const AvisoLegal = () => (
  <LegalWrapper title="Aviso Legal">
    <div><h2 className="text-xl font-semibold text-white mb-2">Titular del sitio web:</h2><p>El presente sitio web es propiedad de Aliados Agency.</p></div>
    <div><h2 className="text-xl font-semibold text-white mb-2">Responsables:</h2><p>Alejandro Olmedilla Soria</p><p>Pedro Gil Elbaz</p></div>
    <div><h2 className="text-xl font-semibold text-white mb-2">Correo electrónico de contacto:</h2><p>{CONTACT.email}</p></div>
    <div><h2 className="text-xl font-semibold text-white mb-2">Ubicación:</h2><p>España</p></div>
    <div><h2 className="text-xl font-semibold text-white mb-2">Objeto:</h2><p>Este sitio web tiene como finalidad ofrecer servicios de marketing digital, creación de contenido y diseño web para negocios.</p></div>
    <div><h2 className="text-xl font-semibold text-white mb-2">Propiedad intelectual:</h2><p>Todos los contenidos del sitio web son propiedad de Aliados Agency o cuentan con licencia, quedando prohibida su reproducción sin autorización expresa.</p></div>
    <div><h2 className="text-xl font-semibold text-white mb-2">Legislación aplicable:</h2><p>La relación entre el usuario y el titular se regirá por la normativa vigente en España.</p></div>
  </LegalWrapper>
);

const PoliticaPrivacidad = () => (
  <LegalWrapper title="Política de Privacidad">
    <div><h2 className="text-xl font-semibold text-white mb-2">Responsables del tratamiento:</h2><p>Alejandro Olmedilla Soria</p><p>Pedro Gil Elbaz</p></div>
    <div><h2 className="text-xl font-semibold text-white mb-2">Correo electrónico:</h2><p>{CONTACT.email}</p></div>
    <div><h2 className="text-xl font-semibold text-white mb-2">Finalidad del tratamiento:</h2><p>Recopilamos datos personales a través del formulario de contacto con el fin de responder a consultas y ofrecer nuestros servicios.</p></div>
    <div><h2 className="text-xl font-semibold text-white mb-2">Legitimación:</h2><p>La base legal para el tratamiento de los datos es el consentimiento del usuario al enviar el formulario.</p></div>
    <div><h2 className="text-xl font-semibold text-white mb-2">Derechos del usuario:</h2><p>El usuario tiene derecho a acceder, rectificar y suprimir sus datos enviando una solicitud al correo electrónico indicado.</p></div>
    <div><h2 className="text-xl font-semibold text-white mb-2">Seguridad:</h2><p>Se adoptan las medidas necesarias para garantizar la seguridad de los datos personales.</p></div>
  </LegalWrapper>
);

const PoliticaCookies = () => (
  <LegalWrapper title="Política de Cookies">
    <p>Este sitio web utiliza cookies para mejorar la experiencia del usuario.</p>
    <div><h2 className="text-xl font-semibold text-white mb-2">¿Qué son las cookies?</h2><p>Las cookies son pequeños archivos de texto que se almacenan en su dispositivo cuando visita un sitio web.</p></div>
    <div><h2 className="text-xl font-semibold text-white mb-2">Tipos de cookies utilizadas:</h2><ul className="list-disc list-inside space-y-2 ml-4"><li><strong>Cookies técnicas:</strong> necesarias para el funcionamiento del sitio web.</li><li><strong>Cookies de análisis:</strong> permiten analizar el comportamiento de los usuarios.</li></ul></div>
    <div><h2 className="text-xl font-semibold text-white mb-2">Gestión de cookies:</h2><p>El usuario puede configurar su navegador para aceptar o rechazar las cookies.</p></div>
    <div><h2 className="text-xl font-semibold text-white mb-2">Más información:</h2><p>Para más información, contacte a: {CONTACT.email}</p></div>
  </LegalWrapper>
);

// ============================================
// SECTION 4b — WHAT WE DO (Qué hacemos)
// ============================================
const WhatWeDo = () => {
  const items = [
    {
      id: "qh-meta", icon: Facebook, icons: [Facebook, Instagram],
      title: "Llega a tu cliente ideal en Facebook e Instagram",
      statNumber: "88,3%",
      statText: "de la población española usa Facebook, y 35,6 millones de personas usan Instagram cada mes en España.",
      source: "Fuente: Digital 2026 (We Are Social) y GfK.",
      text: "Con Meta Ads diseñamos campañas que aparecen exactamente delante de las personas que más probabilidad tienen de convertirse en tus clientes. Segmentación avanzada, creatividades que paran el scroll y optimización constante para que cada euro invertido trabaje al máximo.",
      process: "Antes de lanzar cualquier campaña, analizamos tu negocio de arriba a abajo: tu competencia, tu público objetivo, tu oferta y tu presupuesto. Te entregamos una propuesta personalizada y gratuita con la estrategia, los objetivos y los resultados proyectados. Sin compromiso.",
      cta: "Quiero más clientes con Meta Ads",
    },
    {
      id: "qh-google", icon: Search,
      title: "Aparece en Google cuando tu cliente te está buscando",
      statNumber: "93%",
      statText: "de las experiencias online empiezan con una búsqueda en un buscador.",
      source: "Fuente: estudios del sector.",
      text: "Google Ads te permite estar presente en el momento exacto en que alguien busca lo que tú ofreces. No generamos demanda, capturamos la intención de compra que ya existe. Solo pagas cuando alguien hace clic en tu anuncio.",
      process: "Antes de empezar, realizamos una auditoría gratuita de tus palabras clave y te mostramos exactamente cuántas personas están buscando tu servicio en Google cada mes. Solo pagas cuando alguien hace clic.",
      cta: "Quiero aparecer en Google",
    },
    {
      id: "qh-youtube", icon: Youtube,
      title: "El canal publicitario más potente y menos explotado",
      statNumber: "70%",
      statText: "de los consumidores afirman haber comprado un producto después de verlo en YouTube.",
      source: "Fuente: Think with Google.",
      text: "YouTube es el segundo motor de búsqueda del mundo. Con YouTube Ads llegamos a usuarios con alta intención de compra mientras consumen contenido relacionado con tu sector. Solo pagas cuando el espectador ve tu anuncio completo.",
      process: "Antes de invertir un solo euro, analizamos tu sector y te presentamos qué tipo de campaña generaría mejores resultados para tu negocio.",
      cta: "Quiero crecer con YouTube Ads",
    },
    {
      id: "qh-seo", icon: TrendingUp,
      title: "Posicionamiento en Google para que te encuentren sin pagar por cada clic",
      statNumber: "75%",
      statText: "de los usuarios nunca pasa de la primera página de Google. Si no estás ahí, no existes.",
      source: "Fuente: HubSpot.",
      text: "El SEO es la inversión más rentable a largo plazo. Optimizamos tu web para que aparezca en los primeros resultados de Google de forma orgánica, combinado con SEM para resultados inmediatos mientras el SEO madura.",
      process: "Te hacemos una auditoría gratuita de tu web y tu presencia en Google antes de proponerte nada.",
      cta: "Quiero posicionarme en Google",
    },
    {
      id: "qh-rrss", icon: Instagram,
      title: "Contenido que engancha, comunidad que compra",
      statNumber: "81,4%",
      statText: "de la población española está en redes sociales. Son 39 millones de personas.",
      source: "Fuente: Digital 2026 (We Are Social).",
      text: "Gestionamos tu Instagram y TikTok con estrategia, creatividad y constancia. Grabamos, editamos y publicamos contenido cinematográfico que para el scroll, genera visitas y convierte seguidores en clientes reales.",
      process: "Analizamos tu cuenta actual y la de tu competencia directa y te presentamos un plan de contenido con los formatos que mejor funcionan en tu sector antes de empezar a trabajar juntos.",
      cta: "Quiero más clientes con mis redes",
    },
  ];

  return (
    <section id="que-hacemos" className="relative py-24 px-5 sm:px-6 bg-[#080808] border-y border-white/5">
      <div className="max-w-4xl mx-auto">
        <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-14">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-sky-500 mb-3">Qué hacemos</p>
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight">Publicidad y contenido que traen clientes</h2>
        </motion.div>

        <div className="space-y-6">
          {items.map((it) => (
            <motion.div
              key={it.id}
              id={it.id}
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="bento-card p-7 md:p-10 scroll-mt-28"
              data-testid={`whatwedo-${it.id}`}
            >
              <div className="flex flex-col sm:flex-row items-start gap-5">
                <div className="h-14 min-w-[3.5rem] px-3 rounded-xl bg-sky-500/15 text-sky-400 flex items-center justify-center gap-1.5 shrink-0">
                  {it.icons ? it.icons.map((Ic, k) => <Ic key={k} size={24} />) : <it.icon size={28} />}
                </div>
                <div className="flex-grow">
                  <h3 className="text-xl md:text-2xl font-bold mb-4">{it.title}</h3>
                  <div className="mb-5" data-testid={`whatwedo-stat-${it.id}`}>
                    <span className="text-4xl md:text-5xl font-black text-gradient leading-none">{it.statNumber}</span>
                    <p className="text-sm md:text-base text-zinc-300 mt-2 leading-snug font-medium max-w-lg">{it.statText}</p>
                    {it.source && <p className="text-[11px] text-zinc-500 mt-1.5">{it.source}</p>}
                  </div>
                  <p className="text-zinc-400 leading-relaxed mb-5">{it.text}</p>
                  <div className="rounded-xl bg-sky-500/5 border border-sky-500/20 px-5 py-4 mb-6">
                    <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-sky-400 mb-1.5">Proceso antes de empezar</p>
                    <p className="text-sm text-zinc-400 leading-relaxed">{it.process}</p>
                  </div>
                  <CtaButton size="md" onClick={() => scrollToId("contacto")} testid={`whatwedo-cta-${it.id}`}>
                    {it.cta}
                  </CtaButton>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============================================
// LANDING PAGE
// ============================================
const LandingPage = () => (
  <div className="bg-[#050505] min-h-screen">
    <Header />
    <Hero />
    <SocialProof />
    <Process />
    <Problem />
    <Services />
    <WhatWeDo />
    <WhoWeAre />
    <IntermediateCTA />
    <LeadForm />
    <FinalCTA />
    <Footer />
    <WhatsAppFloat />
  </div>
);

// ============================================
// APP
// ============================================
function App() {
  return (
    <BrowserRouter>
      <div className="App">
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/gracias" element={<GraciasPage />} />
          <Route path="/aviso-legal" element={<AvisoLegal />} />
          <Route path="/privacidad" element={<PoliticaPrivacidad />} />
          <Route path="/cookies" element={<PoliticaCookies />} />
        </Routes>
      </div>
    </BrowserRouter>
  );
}

export default App;
